import { TranslateService } from '@ngx-translate/core';
import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { Router } from '@angular/router';
import {
  HttpClient,
  HttpHeaders,
  HttpParams,
  HttpErrorResponse,
  HttpInterceptor,
  HttpRequest,
  HttpHandler,
  HttpEvent,
} from '@angular/common/http';
import { map, tap } from 'rxjs/operators';
import { environment } from '../../environments/environment';

export interface RequestOptions {
  headers?: HttpHeaders;
  observe?: 'body';
  params?: HttpParams;
  reportProgress?: boolean;
  responseType?: 'json';
  withCredentials?: boolean;
  body?: any;
}

@Injectable()
export class HttpExtendedService {
  private countryCode: string;
  private dealer: string;
  private campaign: string;
  constructor(private http: HttpClient, private translate: TranslateService) {}

  setCountryCode(countryCode): void {
    this.countryCode = countryCode;
  }

  getCountryCode(): string {
    return this.countryCode;
  }

  setDealer(dealer): void {
    this.dealer = dealer;
  }

  getDealer(): string {
    return this.dealer;
  }

  setCampaign(campaign): void {
    this.campaign = campaign;
  }

  getCampaign(): string {
    return this.campaign;
  }

  /**
   * Performs a request with `get` http method.
   */
  public get<T>(
    endPoint: string,
    options = this.baseOptions(),
    args?: any
  ): Observable<T> {
    if (args !== undefined) {
      options.params = args;
    }
    return this.http.get<T>(this.baseUrl() + endPoint, options);
  }

  /**
   * Performs a request with `post` http method.
   */
  post<T>(
    endPoint: string,
    body: any,
    options = this.baseOptions(),

    args?: any
  ): Observable<T> {
    if (args !== undefined) {
      options.params = args;
    }
    return this.http.post<T>(this.baseUrl() + endPoint, body, options);
  }

  /**
   * Performs a request with `put` http method.
   */
  put<T>(
    endPoint: string,
    body: any,
    options = this.baseOptions(),
    args?: any
  ): Observable<T> {
    if (args !== undefined) {
      options.params = args;
    }
    return this.http.put<T>(this.baseUrl() + endPoint, body, options);
  }

  /**
   * Performs a request with `delete` http method.
   */
  delete<T>(endPoint: string, options = this.baseOptions()): Observable<T> {
    return this.http.delete<T>(this.baseUrl() + endPoint, options);
  }

  public baseOptions(): RequestOptions {
    // TODO: remove those options
    const options = {
      headers: new HttpHeaders({ 'Content-type': 'application/json' }),
    };
    return options;
  }

  public blobOptions(): RequestOptions {
    let options: RequestOptions;

    options = {
      headers: new HttpHeaders({ 'Content-type': 'application/json' }),
      responseType: 'blob' as 'json',
      observe: 'response' as 'body',
    };
    return options;
  }

  public baseUrl(): string {
    return `${environment.serverUrl}/api/${this.translate.currentLang}/${this.countryCode}/`;
  }

  private castArgsToParams(args: any): HttpParams {
    const params = new HttpParams();
    for (const propName in args) {
      if (args[propName] !== undefined) {
        params.set(propName, args[propName]);
      }
    }
    return params;
  }
}

export class HttpErrorInterceptor implements HttpInterceptor {
  constructor(
    private router: Router,
    private translate: TranslateService,
    private http: HttpExtendedService
  ) {}
  intercept(
    request: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    return next.handle(request).pipe(
      tap(
        (incoming: any) => {},
        (error: HttpErrorResponse) => {
          // console.log(error);
          const currentLang = this.translate.currentLang;
          let errMsg = '';
          // Client Side Error
          if (error.error instanceof ErrorEvent) {
            errMsg = `Error: ${error.message} `;
          } else {
            // Server Side Error
            errMsg = `Error Code: ${error.status}, Message: ${error.message} `;
            switch (error.status) {
              case 301: // Redirect to start page (Moved Permanently)
                this.router.navigate(
                  [
                    currentLang +
                      '/' +
                      this.http.getCountryCode() +
                      '/vehicle-types',
                  ],
                  {
                    queryParams: { unavailable: 'true' },
                    queryParamsHandling: 'preserve',
                  }
                );
                break;
              case 400: // Bad Request = ApiException
                if (error.error) {
                  const err = error.error.message || error.error;
                  errMsg = err;
                  console.log(errMsg);
                }
                // alert(errMsg);
                // this.router.navigate([currentLang + '/error/' + error.status], {
                //   queryParams: { errMsg },
                //   queryParamsHandling: 'merge',
                // });
                break;
              case 401: // Unauthorized
                this.router.navigate([currentLang + '/error/' + error.status], {
                  queryParamsHandling: 'preserve',
                });
                break;
              case 500: // Internal Server Error
                console.log(error);
                this.router.navigate([currentLang + '/error/' + error.status], {
                  queryParamsHandling: 'preserve',
                });
                break;
              case 403: // Forbidden
              case 404: // Not Found
              case 502: // Bad Gateway
              case 503: // Service Unavailable
              case 504: // Gateway Timeout
                console.log(error);
                this.router.navigate([currentLang + '/error/' + error.status], {
                  queryParamsHandling: 'preserve',
                });
                break;
              default:
                console.log(error);
                this.router.navigate([currentLang + '/error/' + error.status], {
                  queryParamsHandling: 'preserve',
                });
            }
          }
          return of(errMsg);
        }
      )
    );
  }
}

export class RequestInterceptor implements HttpInterceptor {
  constructor(private http: HttpExtendedService) {}
  intercept(
    req: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    if (
      this.http.getDealer() ||
      this.http.getDealer() !== undefined ||
      this.http.getCampaign() ||
      this.http.getCampaign() !== undefined
    ) {
      const dealer = this.http.getDealer();
      const campaign = this.http.getCampaign();

      let headers = new HttpHeaders({ 'Content-type': 'application/json' });

      dealer && (headers = headers.set(dealer && 'dealer', `${dealer}`));
      campaign &&
        (headers = headers.set(campaign && 'campaign', `${campaign}`));

      const modifiedReq = req.clone({ headers });
      return next.handle(modifiedReq);
    }

    return next.handle(req);
  }
}

// tslint:disable-next-line: typedef
export function HttpExtendedFactory(
  http: HttpClient,
  translate: TranslateService
) {
  return new HttpExtendedService(http, translate);
}
